import type { MiniNotice, MiniNoticeKind } from './model';
import { useNarrowViewport } from './useWatchScreen';
import styles from './miniNotice.module.css';

/**
 * What the corner puts where a picture would be: the sentence, or below the `md` step a glyph that
 * says the same thing in the room a 112 px tile has (2026-08-08).
 *
 * The bar shape is a tile and six controls, so the line has nowhere to go but **on** the tile, and
 * there the shortest refusal in the catalogue still wraps to three lines inside 63 px of bar. A
 * picture fits, says the same kind of thing at a glance, and leaves the tile looking like a tile.
 *
 * **The sentence is still spoken**, which is the half of this that an icon cannot do: it stays in
 * the DOM as `srOnly`, so a reader on a phone hears exactly what a reader on a desktop reads, and
 * the container's own `role` is what announces it. An icon is a picture, not a message.
 *
 * The boundary is read from the token (`useNarrowViewport`) as well as from the stylesheet's media
 * query, for `MiniControls`' reason: CSS cannot change what an element *contains*.
 *
 * Which glyph is not decided here — it comes with the sentence, from whichever model produced it
 * (`model.ts`'s `MiniNoticeKind`). Drawing it is this file's, which is the layer's rule for a glyph
 * one component draws; `ui/icons.tsx` keeps only the shared set.
 */
export function MiniNoticeLine({ notice }: { readonly notice: MiniNotice }) {
  const narrow = useNarrowViewport();
  if (!narrow) return <>{notice.text}</>;
  return (
    <>
      <NoticeGlyph kind={notice.kind} />
      <span className={styles.srOnly}>{notice.text}</span>
    </>
  );
}

/**
 * One picture per kind of statement, at the size the tile can hold.
 *
 * `currentColor` throughout, because the two boxes that draw these ink them differently: on the
 * player's tile the line is fixed light ink on a scrim, and in the queue-ended box it is themed text
 * on `surface-raised`.
 */
function NoticeGlyph({ kind }: { readonly kind: MiniNoticeKind }) {
  return (
    <svg
      className={styles.icon}
      viewBox="0 0 24 24"
      width="24"
      height="24"
      aria-hidden="true"
      focusable="false"
    >
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {glyph(kind)}
      </g>
    </svg>
  );
}

function glyph(kind: MiniNoticeKind) {
  switch (kind) {
    /* A circle with a bar through it: the sign for a thing that is not on offer. */
    case 'refusal':
      return (
        <>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M6.4 17.6 17.6 6.4" />
        </>
      );
    /* A clock, because a premiere is a time and not a fault. */
    case 'premiere':
      return (
        <>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M12 7.2V12l3.4 2" />
        </>
      );
    /* A page with a play triangle in it — the video is fine, and that is where it plays. */
    case 'page-only':
      return (
        <>
          <rect x="3.5" y="5" width="17" height="14" rx="2" />
          <path fill="currentColor" stroke="none" d="M10.4 9.4 15.6 12l-5.2 2.6z" />
        </>
      );
    /* A list that stops against a bar, the same bar the step buttons end their triangle with. */
    case 'ended':
      return (
        <>
          <path d="M4.5 7.5h10M4.5 12h10M4.5 16.5h6" />
          <path d="M19 6.5v11" />
        </>
      );
  }
}
